import React from 'react';
import { useTranslation } from 'react-i18next';

import { formaterJour, formaterMois } from './formatAvances';

/**
 * Le parcours d'une note de frais (Yros/Kavero#437) : la dépense, le dépôt,
 * la décision, le remboursement — dans cet ordre, qui est celui de la note.
 *
 * Partagé entre l'espace salarié et la page RH : les deux lisent la même note,
 * et deux présentations des mêmes dates finiraient par se contredire.
 *
 * Chaque étape porte son état dans `data-etat` — fait, attente, refus,
 * annule — et le point de la frise le montre : rempli quand c'est fait, creux
 * quand on attend, rouge pour un refus.
 */

const POINT = {
  fait: 'bg-[--color-primary] border-[--color-primary]',
  attente: 'bg-white border-slate-300',
  refus: 'bg-red-600 border-red-600',
  annule: 'bg-white border-slate-200',
};

const COULEUR_VERSEMENT = {
  verse: 'text-green-700',
  calcule: 'text-sky-700',
};

function etapeDecision(demande, locale, t) {
  const P = 'notesFrais.parcours';
  if (demande.etat === 'refusee') {
    return { cle: 'decision', libelle: t(`${P}.refus`), valeur: formaterJour(demande.date_decision, locale), etat: 'refus' };
  }
  if (demande.etat === 'validee') {
    return { cle: 'decision', libelle: t(`${P}.validation`), valeur: formaterJour(demande.date_decision, locale), etat: 'fait' };
  }
  return { cle: 'decision', libelle: t(`${P}.validation`), valeur: t(`${P}.enAttente`), etat: 'attente' };
}

function etapeRemboursement(demande, locale, t) {
  const P = 'notesFrais.parcours';
  const base = { cle: 'remboursement', libelle: t(`${P}.remboursement`) };
  if (demande.etat === 'refusee') {
    return { ...base, valeur: t(`${P}.sansObjet`), etat: 'annule' };
  }
  const versement = demande.etat === 'validee' ? demande.contenu?.versement : null;
  if (!versement) {
    return { ...base, valeur: demande.etat === 'validee' ? '—' : t(`${P}.apresValidation`), etat: 'attente' };
  }
  return {
    ...base,
    valeur: (
      <span className="flex flex-col items-end">
        <span className="capitalize">{formaterMois(versement.mois, locale)}</span>
        <span className={`text-xs ${COULEUR_VERSEMENT[versement.statut] || 'text-slate-500'}`}>
          {t(`notesFrais.versement.${versement.statut}`, { defaultValue: versement.statut })}
        </span>
      </span>
    ),
    etat: versement.statut === 'verse' ? 'fait' : 'attente',
  };
}

function ParcoursNoteFrais({ demande, locale, testId, testIdVersement }) {
  const { t } = useTranslation();
  const etapes = [
    {
      cle: 'depense',
      libelle: t('notesFrais.parcours.depense'),
      valeur: formaterJour(demande.contenu?.date_depense, locale),
      etat: 'fait',
    },
    {
      cle: 'depot',
      libelle: t('notesFrais.parcours.depot'),
      valeur: formaterJour(demande.date_demande, locale),
      etat: 'fait',
    },
    etapeDecision(demande, locale, t),
    etapeRemboursement(demande, locale, t),
  ];
  const derniere = etapes.length - 1;

  return (
    <ol aria-label={t('notesFrais.parcours.titre')} data-testid={testId} className="space-y-1.5">
      {etapes.map((etape, index) => (
        <li
          key={etape.cle}
          data-etat={etape.etat}
          className="relative grid grid-cols-[0.75rem_minmax(0,1fr)] gap-x-2.5"
        >
          <span aria-hidden="true" className="relative flex justify-center">
            <span className={`relative z-10 mt-1 h-2.5 w-2.5 rounded-full border-2 ${POINT[etape.etat]}`} />
            {index < derniere && (
              <span className="absolute top-3.5 -bottom-2 w-px bg-slate-200" />
            )}
          </span>
          {/* Libellé à gauche, valeur à droite, toujours sur la même ligne :
              une frise dont les dates sautent d'une ligne à l'autre ne se lit
              plus d'un coup d'œil. */}
          <div className="min-w-0 flex items-start justify-between gap-3">
            <span className="pt-px text-xs text-slate-500">{etape.libelle}</span>
            <span
              data-testid={etape.cle === 'remboursement' ? testIdVersement : undefined}
              className={`text-right text-sm whitespace-nowrap tabular-nums ${etape.etat === 'attente' && etape.cle === 'decision' ? 'text-amber-700' : 'text-slate-900'}`}
            >
              {etape.valeur}
            </span>
          </div>
        </li>
      ))}
    </ol>
  );
}

export default ParcoursNoteFrais;
